/**
 * vehicleZoneMove.ts — 「車両を◯◯へ移す」ときの決まりごと（2026-08-25）
 *
 * 車両ポジションの DnD と、貸出返却リストの車両ステータス変更は、もともと
 * まったく別の実装だった。そのため同じ「迎え中にする」でも
 *   ・移動ステータスが入る／入らない
 *   ・運転者を聞かれる／聞かれない
 *   ・予約が貸出済みになる／ならない
 * と挙動がバラバラになっていた。
 *
 * 移動先（ゾーン）ごとの決まりごとをここに1つだけ置き、両方がこれを参照する。
 * ゾーンの構成は店舗別（config/vehicleZones.ts）なので、那覇の赤嶺駅・宮古の
 * 下地島も自動的に同じ扱いになる。
 */
import { getVehicleZonesConfig } from '../config/vehicleZones';

export interface ZoneMovePlan {
  /** 移動先ゾーン id */
  zoneId: string;
  /** 画面に出す行き先名（ゾーンラベルの改行を空白に均したもの） */
  label: string;
  /** 車両ステータス code */
  statusCode: string;
  /** 移動ステータス code（無いゾーンは null）。id は店舗ごとに採番が違うので実行時に解決する */
  locationStatusCode: string | null;
  /** 移動後に運転者を聞く相手。ピックアップ=貸出 / ドロップオフ=返却 / それ以外=聞かない */
  driverType: 'departure' | 'return' | null;
}

const cfg = getVehicleZonesConfig();

/** 車両を置けるゾーンだけを対象にする（airport_1f 等のラベル専用ゾーンは除く） */
const droppableZones = cfg.zones.filter(z => z.droppable !== false && !!z.statusCode);

const planOf = (zone: typeof droppableZones[number]): ZoneMovePlan => ({
  zoneId: zone.id,
  label: zone.label.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim(),
  statusCode: zone.statusCode as string,
  locationStatusCode: cfg.zoneLocationStatus[zone.id] ?? null,
  driverType: cfg.pickupZoneIds.has(zone.id)
    ? 'departure'
    : cfg.dropoffZoneIds.has(zone.id)
      ? 'return'
      : null,
});

/** ゾーン id から移動プランを引く（車両ポジションの DnD 側で使う） */
export const getZoneMovePlan = (zoneId: string): ZoneMovePlan | null => {
  const zone = droppableZones.find(z => z.id === zoneId);
  return zone ? planOf(zone) : null;
};

/**
 * リストで車両ステータスを選んだときの「行き先」候補。
 *
 * ピックアップ系（迎え中 / 赤嶺駅P.U）とドロップオフ系（送り中 / 赤嶺駅D.O）は、
 * 同じ系統の中に行き先が複数あるので候補をすべて返す。呼び出し側は
 *   2件以上 → どこへ行くか聞く
 *   1件     → そのまま適用（移動ステータスも入る）
 *   0件     → ゾーンに対応しないステータス（修理・米軍など）。ステータスだけ変える
 * とする。
 *
 * 「迎え中」を選んでも赤嶺駅が候補に出るのは意図的。赤嶺駅で拾う場合は
 * 車両ステータス自体が 赤嶺駅P.U になるのが正しく（車両ポジションのエリアと
 * 一致させるため）、行き先を選べばステータスも一緒に決まる。
 */
export const getMoveChoicesForStatusCode = (statusCode: string): ZoneMovePlan[] => {
  const family = droppableZones.some(z => z.statusCode === statusCode && cfg.pickupZoneIds.has(z.id))
    ? cfg.pickupZoneIds
    : droppableZones.some(z => z.statusCode === statusCode && cfg.dropoffZoneIds.has(z.id))
      ? cfg.dropoffZoneIds
      : null;
  if (family) return droppableZones.filter(z => family.has(z.id)).map(planOf);

  // ピックアップ/ドロップオフ以外（店舗・ヤード・三井P・修理・米軍…）は、
  // リストからの変更を DnD に寄せるかどうかを未決のまま据え置く。
  // 今は候補なし＝従来どおりステータスだけ変える（2026-08-25）。
  return [];
};
